import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiZap, FiAlertCircle, FiImage, FiLoader, FiUploadCloud, FiCloud, FiX } from 'react-icons/fi';
import supabase, { withRetry } from '../../supabase/supabase';
import { compressImage } from '../../utils/imageCompression';

function AIImageGenerator({ onSelect, onClose }) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [error, setError] = useState('');

  const saveAIImageToLibrary = async (imageUrl) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const response = await fetch(imageUrl, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) throw new Error('Failed to fetch the generated image.');
      const blob = await response.blob();
      const fileName = `ai-image-${Date.now()}-${Math.random().toString(36).substring(2, 8)}.png`;
      const filePath = `images/${fileName}`;
      
      const result = await withRetry(() => supabase.storage.from('media').upload(filePath, blob, { cacheControl: '3600', upsert: false, contentType: 'image/png' }), 2, 1000, 15000);
      if (result.error) throw result.error;
      
      const { data: publicUrlData } = supabase.storage.from('media').getPublicUrl(filePath);
      return publicUrlData?.publicUrl || imageUrl;
    } catch (err) {
      console.error('Failed to save AI image to library:', err);
      return imageUrl;
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError('');
    setGeneratedUrl('');
    try {
      const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
      if (!apiKey) throw new Error('OpenAI API key not configured.');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25000);
      
      const response = await fetch('https://api.openai.com/v1/images/generations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({ model: 'dall-e-3', prompt, n: 1, size: '1792x1024', quality: 'standard' }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error?.message || `API error ${response.status}`);
      }
      const data = await response.json();
      const url = data?.data?.[0]?.url;
      if (!url) throw new Error('No image returned from AI.');
      const savedUrl = await saveAIImageToLibrary(url);
      setGeneratedUrl(savedUrl);
    } catch (err) {
      setError(err.name === 'AbortError' ? 'Image generation timed out.' : (err.message || 'Failed to generate image.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-2 border-dashed border-[#c8f542]/40 bg-[#c8f542]/5 rounded-2xl p-5 space-y-4">
      <div className="flex items-center space-x-2">
        <div className="w-6 h-6 bg-[#c8f542] rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiZap} className="w-3.5 h-3.5 text-black" />
        </div>
        <span className="text-[10px] font-black text-gray-700 uppercase tracking-widest">Generate with AI</span>
      </div>
      <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="e.g. A vibrant South African market scene at golden hour..." rows={3} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm font-medium text-gray-800 placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#c8f542]/40 focus:border-[#c8f542]/60 transition-all resize-none" />
      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start space-x-2 text-red-600 text-xs font-medium"><SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0 mt-0.5" /><span>{error}</span></div>}
      {generatedUrl && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          <div className="relative rounded-xl overflow-hidden aspect-video border-2 border-[#c8f542]/40 shadow-lg">
            <img src={generatedUrl} alt="AI Generated" className="w-full h-full object-cover" />
            <div className="absolute top-2 left-2 bg-[#c8f542] text-black text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full">✓ Saved to Library</div>
          </div>
          <div className="flex space-x-2">
            <button onClick={() => { onSelect({ url: generatedUrl, name: 'ai-generated-image' }); onClose(); }} className="flex-1 py-2.5 bg-[#c8f542] hover:bg-[#d4ff50] text-black font-black text-xs uppercase tracking-widest rounded-xl transition-all flex items-center justify-center space-x-1.5">
              <SafeIcon icon={FiImage} className="w-3.5 h-3.5" /><span>Use This Image</span>
            </button>
            <button onClick={() => { setGeneratedUrl(''); setError(''); }} className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold text-xs rounded-xl transition-all">Discard</button>
          </div>
        </motion.div>
      )}
      {!generatedUrl && (
        <button onClick={handleGenerate} disabled={loading || !prompt.trim()} className="w-full py-3 bg-gray-900 hover:bg-black text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
          {loading ? (<><SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" /><span>Generating & Saving...</span></>) : (<><SafeIcon icon={FiZap} className="w-4 h-4" /><span>Generate Image</span></>)}
        </button>
      )}
    </div>
  );
}

export default function ImageWidgetPopup({ onClose, onSelect, onOpenLibrary }) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [activeTab, setActiveTab] = useState('upload');

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError('');
    if (!file.type.startsWith('image/')) { setUploadError('Invalid file type.'); return; }
    if (file.size > 15 * 1024 * 1024) { setUploadError('File too large. Max 15MB.'); return; }
    
    setIsUploading(true);
    setUploadProgress(10);
    try {
      const compressedFile = await compressImage(file, { maxWidth: 1920, maxHeight: 1920, quality: 0.8 });
      setUploadProgress(30);
      const fileExt = compressedFile.name.split('.').pop() || 'png';
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
      const filePath = `images/${fileName}`;
      
      const result = await withRetry(() => supabase.storage.from('media').upload(filePath, compressedFile, { cacheControl: '3600', upsert: false }), 2, 1000, 15000);
      if (result.error) throw new Error(result.error.message || 'Failed to upload file.');
      
      setUploadProgress(80);
      const { data: publicUrlData } = supabase.storage.from('media').getPublicUrl(filePath);
      if (!publicUrlData?.publicUrl) throw new Error('Failed to get public URL.');
      
      setUploadProgress(100);
      onSelect({ url: publicUrlData.publicUrl, name: fileName });
      onClose();
    } catch (error) {
      console.error('File Upload Error:', error);
      setUploadError(error.message || 'An unexpected error occurred during upload.');
    } finally {
      setTimeout(() => { setIsUploading(false); setUploadProgress(0); if (e.target) e.target.value = ''; }, 500);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/80">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-2xl"><SafeIcon icon={FiImage} className="w-6 h-6 text-blue-600" /></div>
            <div><h3 className="font-black text-gray-900 text-xl uppercase tracking-tight">Add Image</h3></div>
          </div>
          <button onClick={onClose} className="bg-white p-2.5 rounded-xl shadow-sm hover:shadow-md transition-all text-gray-400 hover:text-gray-600 border border-gray-100"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
        </div>
        <div className="flex p-1.5 bg-gray-100 mx-6 mt-5 rounded-2xl">
          {[{ id: 'upload', label: 'Upload / Library' }, { id: 'ai', label: '✦ Generate with AI' }].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${activeTab === tab.id ? 'bg-white shadow-lg text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="p-6 space-y-4 max-h-[65vh] overflow-y-auto custom-scrollbar">
          <AnimatePresence mode="wait">
            {activeTab === 'upload' ? (
              <motion.div key="upload" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-4">
                {uploadError && <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-3 flex items-start space-x-3"><SafeIcon icon={FiAlertCircle} className="w-5 h-5 shrink-0 mt-0.5" /><div className="text-xs font-medium">{uploadError}</div></div>}
                <label className={`flex flex-col items-center justify-center aspect-video border-4 border-dashed rounded-3xl cursor-pointer transition-all ${isUploading ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200 hover:border-blue-400 hover:bg-blue-50/50'}`}>
                  <div className="flex flex-col items-center space-y-4 px-6 text-center w-full">
                    <div className={`p-5 rounded-2xl ${isUploading ? 'bg-blue-500 text-white' : 'bg-white text-gray-300 shadow-sm'}`}>
                      <SafeIcon icon={isUploading ? FiUploadCloud : FiImage} className={`w-8 h-8 ${isUploading ? 'animate-bounce' : ''}`} />
                    </div>
                    <span className="text-sm font-black text-gray-900 block">{isUploading ? 'Uploading...' : 'Drop your image here'}</span>
                    {isUploading && <div className="w-full max-w-[200px] bg-blue-100 h-1.5 rounded-full overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${uploadProgress}%` }} className="bg-blue-500 h-full" /></div>}
                  </div>
                  <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleFileUpload} disabled={isUploading} />
                </label>
                <button onClick={() => onOpenLibrary((img) => { onSelect({ url: img.url, name: img.name }); onClose(); })} className="w-full py-4 border-2 border-dashed border-blue-200 bg-blue-50/50 rounded-2xl flex items-center justify-center space-x-3 hover:border-blue-400 hover:bg-blue-50 transition-all">
                  <SafeIcon icon={FiCloud} className="w-5 h-5 text-blue-500" />
                  <span className="font-bold text-blue-600">Choose from Media Library</span>
                </button>
              </motion.div>
            ) : (
              <motion.div key="ai" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}>
                <AIImageGenerator onSelect={onSelect} onClose={onClose} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}